// src/components/quizzes/BonusMultiplyFractions.js
//
// Multiply fractions bonus round: catch the product.
// Runs on the shared bonus-round engine (kit/BonusShell.js) — the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, a new question
// every few right answers, and 🏁 End game. Same contract as before:
// BonusRouter renders <ThisGame onBonusComplete questions placeholderImage />.

import React from "react";
import BonusShell, { Frac } from "./kit/BonusShell";

const SPAWN_MS = 500;
const MIN_TARGETS_ONSCREEN = 2;    // at least this many correct options on screen
const TARGET_SPEED_SLOWDOWN = 0.9; // correct values fall a bit slower

/** ======= MATH HELPERS ======= */
const gcd = (a, b) => {
  a = Math.abs(a); b = Math.abs(b);
  while (b !== 0) { const t = b; b = a % b; a = t; }
  return a || 1;
};

const simplify = ({ n, d }) => {
  if (d === 0) return { n: 0, d: 1 };
  const g = gcd(n, d);
  let sn = n / g, sd = d / g;
  // keep denominator positive
  if (sd < 0) { sn = -sn; sd = -sd; }
  return { n: sn, d: sd };
};

const fracEq = (a, b) => a.n * b.d === a.d * b.n;

const fracToStr = ({ n, d }) => `${n}/${d}`;

/** ======= RANDOMS ======= */
const randomInt = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const RAND_DEN_POOL = [2,3,4,5,6,8,9,10,12]; // nice classroom denominators

const randProperFrac = () => {
  const d = pick(RAND_DEN_POOL);
  const n = randomInt(1, d - 1);
  return simplify({ n, d });
};

/** Problem model:
 * {
 *   a:{n,d}, b:{n,d},
 *   prod:{n,d} (simplified),
 *   prodStr:"n/d"
 * }
 */
const makeRandomProblem = () => {
  const a = randProperFrac();
  const b = randProperFrac();
  const prod = simplify({ n: a.n * b.n, d: a.d * b.d });
  return { a, b, prod, prodStr: fracToStr(prod) };
};

const ruleText = () => `the product as a fraction`;

/** Build plausible distractors (not equal to product) */
const makeDistractors = (p) => {
  const { a, b, prod } = p;
  const pool = new Set();

  const pushFrac = (n, d) => {
    if (d === 0) return;
    const s = fracToStr(simplify({ n, d }));
    // exclude true answer
    if (!fracEq(simplify({ n, d }), prod)) pool.add(s);
  };

  // common mistakes:
  // 1) add numerators / add denominators
  pushFrac(a.n + b.n, a.d + b.d);

  // 2) cross mix: a.n*b.n over a.d + b.d
  pushFrac(a.n * b.n, a.d + b.d);

  // 3) one-step wrong: a.n*b.n over a.d*b.d +/- 1
  pushFrac(a.n * b.n, a.d * b.d + 1);
  pushFrac(a.n * b.n, a.d * b.d - 1);

  // 4) multiply only numerators or only denominators
  pushFrac(a.n * b.n, a.d);     // forgot to multiply denominators
  pushFrac(a.n, a.d * b.d);     // forgot to multiply numerators

  // 5) near-by tweaks of the true product
  pushFrac(prod.n + 1, prod.d);
  pushFrac(prod.n - 1, prod.d);
  pushFrac(prod.n, prod.d + 1);
  pushFrac(prod.n, prod.d - 1);

  // 6) swap numbers
  pushFrac(a.n * b.d, a.d * b.n);

  return Array.from(pool);
};

/** Keep mix balanced and ensure some correct are present */
const shouldSpawnCorrectBalanced = (problem, items) => {
  const correct = items.filter(it => it.value === problem.prodStr).length;
  const wrong = items.length - correct;
  if (correct < MIN_TARGETS_ONSCREEN) return true;
  if (correct < wrong) return true;
  if (wrong < correct) return false;
  return Math.random() < 0.5;
};

const newProblem = () => {
  const p = makeRandomProblem();
  return { ...p, distractors: makeDistractors(p) };
};
const fracLabel = (s) => {
  const [n, d] = String(s).split("/");
  return <Frac n={n} d={d} />;
};

const MultiplyFractionsBonus = (props) => (
  <BonusShell
    {...props}
    title="Multiply Fractions!"
    emoji="✖️"
    sub="Multiply the fractions — then catch the product."
    intro="Multiply the fractions. Catch the product in simplest form. Each one is worth five points. Watch out for bombs! Press Start when you are ready."
    rules={[
      { icon: "✅", text: "The product", pts: "+5" },
      { icon: "❌", text: "Wrong answer", pts: "-1" },
    ]}
    init={newProblem}
    prompt={(p) => ({
      big: (
        <>
          <Frac n={p.a.n} d={p.a.d} /> × <Frac n={p.b.n} d={p.b.d} /> = ?
        </>
      ),
      small: `Catch ${ruleText()}!`,
      say: `What is ${p.a.n} over ${p.a.d} times ${p.b.n} over ${p.b.d}?`,
    })}
    spawn={(p, onScreen) => (shouldSpawnCorrectBalanced(p, onScreen) ? p.prodStr : pick(p.distractors.length ? p.distractors : ["1/2", "2/3", "3/5", "4/7"]))}
    check={(p, v) => v === p.prodStr}
    label={fracLabel}
    newEvery={3}
    spawnMs={SPAWN_MS}
    slowGood={TARGET_SPEED_SLOWDOWN}
  />
);

export default MultiplyFractionsBonus;
